import axios, { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { IUser, IUserFormValues } from "../models/user";
import { history } from "../..";
import { IProject } from "../models/project";
import { IProjectTask } from "../models/projecttask";
import { ISORList } from "../models/sorlist";
import { ITechnician } from "../models/technician";
import { ITaskTechnician } from "../models/tasktechnician";
import { IProjectLog } from "../models/projectlog";
import { IWarehouse } from "../models/warehouse";
import { IInvoice } from "../models/invoice";
import { ITechnicianRate } from "../models/technicianrate";
import { IWarehouseLog } from "../models/warehouselog";
import { IProjectStock } from "../models/projectstock";
import { ICertificate } from "../models/certificate";
import { ITechnicianCertificate } from "../models/techniciancertificate";
import { IThirdparty } from "../models/thirdparty";
import { IProjectVendor } from "../models/projectvendor";
import { IFile } from "../models/file";
import { IPhotoRequest } from "../models/photorequest";
import { IProjectPhoto } from "../models/projectphoto";
import { IPhotoUpload } from "../models/photoupload";
import { IPhoto, IPhotoUrl } from "../models/photo";
import { IEmployee } from "../models/employee";
import { ITaskAssignment } from "../models/taskassignment";
import { ITechnicianSOR } from "../models/techniciansor";
import { IFttbHour } from "../models/fttbhour";
import { IPayrollCycle } from "../models/payrollcycle";
import { ITechnicianCertificatePhoto } from "../models/techniciancertificatephoto";
import { IBdodJob } from "../models/bdodjob";
import { request } from "http";
import { ISiteVodafone } from "../models/sitevodafone";
import { IUnifyJob } from "../models/unifyjob";
import { ISiteVodafoneHistory } from "../models/sitevodafonehistory";
import { IFttcHour } from "../models/fttchour";
import { ICustomer } from "../models/customer";
import { ITransmission } from "../models/transmission";
import { IProjectScope } from "../models/projectscope";
import { IAsset } from "../models/asset";
import { ITransmissionChecklist } from "../models/transmissionchecklist";
import { ITransmissionProjectWithFile } from "../models/transmissionProjectWithFile";
import { ITechnicianCertificateName } from "../models/techniciancertificatename";
import { IProjectNote } from "../models/projectnote";
import { IAssign } from "../models/assign";
import { IProjectPermit } from "../models/projectpermit";
import { IProjectBog } from "../models/projectbog";
import { ITimeSheet } from "../models/timesheet";
import { IProjectDocument } from "../models/projectdocument";
import { IJobPosition } from "../models/jobposition";
import { IBogReport } from "../models/bogreport";
import { IProjectTime } from "../models/projecttime";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

axios.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem("jwt");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(undefined, (error) => {
  if (error.message === "Network Error" && !error.response) {
    toast.error("Network error - make sure API is running!");
  }
  const { status, data, config, headers } = error.response;
  if (status === 404 || status === 400) {
    toast.error(data.errors.message);
    // alert(data.errors.message);
  }
  if (
    status === 401 &&
    headers["www-authenticate"] ===
      'Bearer error="invalid_token", error_description="The token is expired"'
  ) {
    window.localStorage.removeItem("jwt");
    history.push("/");
    toast.info("Your session has expired, please login again");
  }
  // if (
  //   status === 400 &&
  //   config.method === 'get' &&
  //   data.errors.hasOwnProperty('id')
  // ) {
  //   history.push('/notfound');
  // }
  if (status === 500) {
    toast.error("Server error - check the terminal for more info!");
  }
  throw error.response;
});

const responseBody = (response: AxiosResponse) => response.data;

const sleep = (ms: number) => (response: AxiosResponse) =>
  new Promise<AxiosResponse>((resolve) =>
    setTimeout(() => resolve(response), ms)
  );

const requests = {
  get: (url: string) => axios.get(url).then(sleep(10)).then(responseBody),
  post: (url: string, body: {}) =>
    axios.post(url, body).then(sleep(10)).then(responseBody),
  put: (url: string, body: {}) =>
    axios.put(url, body).then(sleep(10)).then(responseBody),
  del: (url: string) => axios.delete(url).then(sleep(10)).then(responseBody),
  postForm: (url: string, file: File[], name: string) => {
    let formData = new FormData();
    for (let i = 0; i < file.length; i++) {
      formData.append("File", file[i]);
    }
    formData.append("Name", name);
    return axios
      .post(url, formData, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody);
  },
  postFormPhoto: (url: string, photo: File[], name: string, photoCategory: string
  ) => {
    let formData = new FormData();
    for (let i = 0; i < photo.length; i++) {
      formData.append("File", photo[i]);
    }
    formData.append("Name", name);
    formData.append("PhotoCategory", photoCategory);
    return axios
      .post(url, formData, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody);
  },
  postFormAsset: (url: string, file: File[], asset: IAsset) => {
    let formData = new FormData();
    for (let i = 0; i < file.length; i++) {
      formData.append("File", file[i]);
    }
    formData.append("Id", asset.id);
    formData.append("Code", asset.code);
    formData.append("AssetName", asset.assetName);
    formData.append("Type", asset.type);
    formData.append("Model", asset.model);
    formData.append("Price", asset.price.toString());
    formData.append("PurchaseDate", asset.purchaseDate);
    formData.append("Status", asset.status);
    formData.append("Life", asset.life);
    formData.append("InspectionDate", asset.inspectionDate);
    formData.append(
      "InspectionFrequency",
      asset.inspectionFrequency.toString()
    );
    formData.append("ServiceDate", asset.serviceDate);
    formData.append("RegoDate", asset.regoDate);
    formData.append("InsuranceDate", asset.insuranceDate);
    formData.append("PhotoUrl", asset.photoUrl[0]);
    formData.append("Remark", asset.remark);
    formData.append("KeepId", asset.keep[0].id);
    formData.append("Keeper", asset.keep[0].keeper);
    formData.append("KeepDate", asset.keep[0].keepDate);
    return axios
      .post(url, formData, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody);
  },
  postFormTransmissionPhoto: (url: string, photo: File[], id: string, siteId: string, photoCategory: string) => 
    {
      let formData = new FormData();
      for (let i = 0; i < photo.length; i++) {
        formData.append("File", photo[i]);
      }
      formData.append("Id", id);
      formData.append("SiteId", siteId);
      formData.append("PhotoCategory", photoCategory);
      return axios
        .post(url, formData, {
          headers: { "Content-type": "multipart/form-data" },
        })
        .then(responseBody);
    },
  postFormEmployeePhoto: (url: string, photo: File, email: string
      ) => {
        let formData = new FormData();
        formData.append("Photo", photo);
        formData.append("Email", email);
        return axios
          .post(url, formData, {
            headers: { "Content-type": "multipart/form-data" },
          })
          .then(responseBody);
      },
};

const User = {
  current: (): Promise<IUser> => requests.get("/user"),
  login: (user: IUserFormValues): Promise<IUser> =>
    requests.post(`/user/login`, user),
  register: (user: IUserFormValues): Promise<IUser> =>
    requests.post(`/user/register`, user),
  resetpwd: (user: IUserFormValues): Promise<IUser> =>
    requests.post(`/user/resetpwd`, user),
  verifyEmail: (token: string, email: string): Promise<void> =>
    requests.post(`/user/verifyEmail`, { token, email }),
  resendVerifyEmailConfirm: (email: string): Promise<void> =>
    requests.get(`/user/resendEmailVerification?email=${email}`),
};

const Projects = {
  list: (type: string, status: string): Promise<IProject[]> => 
    requests.get(`/projects?type=${type}&status=${status}`),
  listStatus: (status: string): Promise<IProject[]> =>
    requests.get(`/projects/${status}`),
  details: (id: string) => requests.get(`/projects/${id}`),
  create: (project: IProject) => requests.post("/projects", project),
  update: (project: IProject) =>
    requests.put(`/projects/${project.id}`, project),
  delete: (id: string) => requests.del(`/projects/${id}`),
  generate: () => requests.get(`/projects/code`),
};

const ProjectTasks = {
  list: (projectId: string): Promise<IProjectTask[]> =>
    requests.get(`/projecttasks/${projectId}`),
  details: (id: string) => requests.get(`/projecttasks/${id}`),
  create: (projecttask: IProjectTask[]) =>
    requests.post("/projecttasks", projecttask),
  update: (projecttask: IProjectTask) =>
    requests.put(`/projecttasks/${projecttask.id}`, projecttask),
  delete: (id: string) => requests.del(`/projecttasks/${id}`),
};

const SORLists = {
  list: (): Promise<ISORList[]> => requests.get("/sorlists"),
  //details: (name: string) => requests.get(`/sorlists/${name}`),
  sort: (category: string): Promise<ISORList[]> =>
    requests.get(`/sorlists/${category}`),
  create: (sorlist: ISORList) => requests.post("/sorlists", sorlist),
  update: (sorlist: ISORList) =>
    requests.put(`/sorlists/${sorlist.name}`, sorlist),
  delete: (name: string) => requests.del(`/sorlists/${name}`),
};

const Customers = {
  list: (): Promise<ICustomer[]> => requests.get("/customers"),
  details: (id: string) => requests.get(`/customers/${id}`),
  create: (customer: ICustomer) => requests.post("/customers", customer),
  update: (customer: ICustomer) =>
    requests.put(`/customers/${customer.id}`, customer),
  delete: (id: string) => requests.del(`/customers/${id}`),
};

const TaskAssignments = {
  list: (projectId: string): Promise<ITaskAssignment[]> =>
    requests.get(`/taskassignments/${projectId}`),
  create: (tasktechnician: ITaskAssignment) =>
    requests.post("/taskassignments", tasktechnician),
  update: (tasktechnician: ITaskAssignment[]) =>
    requests.put(`/taskassignments`, tasktechnician),
  select: (): Promise<IProject[]> => requests.get("/taskassignments"),
  delete: (id: string) => requests.del(`/taskassignments/${id}`),
  updateInduction: (projectId: string, email: string, status: string) => 
    requests.put(`/taskassignments/${projectId}/${email}`,{status: status}),
};

const MobileAssignments = {
  list: (projectId: string) =>
    requests.get(`/mobileassignments/${projectId}`),
  create: (projectId: string, mobileassignment: any) =>
    requests.post(`/mobileassignments/${projectId}`, mobileassignment),
};

const ProjectLogs = {
  list: (projectId: string): Promise<IProjectLog[]> =>
    requests.get(`/projectlogs/${projectId}`),
  // details: (id: string) => requests.get(`/projecttasks/${id}`),
  create: (projectlog: IProjectLog) =>
    requests.post("/projectlogs", projectlog),
  // update: (projecttask: IProjectTask) => requests.put(`/projecttasks/${projecttask.id}`, projecttask),
  delete: (id: string) => requests.del(`/projectlogs/${id}`),
};

const ProjectPermits = {
  list: (projectId: string): Promise<IProjectPermit[]> =>
    requests.get(`/projectpermits/${projectId}`),
  create: (projectpermit: IProjectPermit) =>
    requests.post("/projectpermits", projectpermit),
  delete: (id: string) => requests.del(`/projectpermits/${id}`), 
};

const ProjectNotes = {
  list: (projectId: string): Promise<IProjectNote[]> =>
    requests.get(`/projectnotes/${projectId}`),
  // details: (id: string) => requests.get(`/projecttasks/${id}`),
  create: (projectnote: IProjectNote) =>
    requests.post("/projectnotes", projectnote),
  update: (id: string, status: string) => requests.put(`/projectnotes/${id}`, {"status": status}),
  delete: (id: string) => requests.del(`/projectnotes/${id}`),
};

const Warehouses = {
  list: (): Promise<IWarehouse[]> => requests.get("/warehouses"),
  details: (id: string) => requests.get(`/warehouses/${id}`),
  create: (warehouse: IWarehouse) => requests.post("/warehouses", warehouse),
  update: (warehouse: IWarehouse) =>
    requests.put(`/warehouses/${warehouse.id}`, warehouse),
  delete: (id: string) => requests.del(`/warehouses/${id}`),
};

const Invoices = {
  list: (): Promise<IInvoice[]> => requests.get("/invoices"),
  details: (id: string) => requests.get(`/invoices/${id}`),
  create: (invoice: IInvoice) => requests.post("/invoices", invoice),
  update: (invoice: IInvoice) =>
    requests.put(`/invoices/${invoice.id}`, invoice),
  delete: (id: string) => requests.del(`/invoices/${id}`),
};

const Employees = {
  list: (): Promise<IEmployee[]> => requests.get("/employees"),
  details: (email: string) => requests.get(`/employees/${email}`),
  create: (employee: IEmployee) => requests.post("/employees", employee),
  update: (employee: IEmployee, email: string) =>
    requests.put(`/employees/${email}`, employee),
  delete: (email: string) => requests.del(`/employees/${email}`),
  uploadEmployeePhoto: (photo: File, email: string): Promise<IPhotoUrl> => 
    requests.postFormEmployeePhoto(`/employees/photo`, photo, email),
  deleteEmployeePhoto: (email: string) => requests.del(`/employees/photo/${email}`),
  download: (email: string, certificateId: {id: string}[]): Promise<IFile> =>
    requests.put(`/employees/download/${email}`, certificateId),
};

const WarehouseLogs = {
  list: (projectId: string): Promise<IWarehouseLog[]> =>
    requests.get(`/warehouselogs/${projectId}`),
  create: (warehouselog: IWarehouseLog[]) =>
    requests.post("/warehouselogs", warehouselog),
  update: (warehouselog: IWarehouseLog) =>
    requests.put(`/warehouselogs/${warehouselog.id}`, warehouselog),
  delete: (id: string) => requests.del(`/warehouselogs/${id}`),
};

const ProjectStocks = {
  list: (projectId: string): Promise<IProjectStock[]> =>
    requests.get(`/projectstocks/${projectId}`),
  // details: (id: string) => requests.get(`/projecttasks/${id}`),
  // create: (projectlog: IProjectLog) => requests.post('/projectlogs', projectlog),
  // update: (projecttask: IProjectTask) => requests.put(`/projecttasks/${projecttask.id}`, projecttask),
  // delete: (id: string) => requests.del(`/projecttasks/${id}`)
};

const Certificates = {
  list: (): Promise<ICertificate[]> => requests.get("/certificates"),
  //details: (id: string) => requests.get(`/certificates/${id}`),
  sort: (category: string): Promise<ICertificate[]> =>
    requests.get(`/certificates/${category}`),
  create: (certificate: ICertificate) =>
    requests.post("/certificates", certificate),
  update: (certificate: ICertificate) =>
    requests.put(`/certificates/${certificate.id}`, certificate),
  delete: (id: string) => requests.del(`/certificates/${id}`),
};

const TechnicianCertificates = {
  list: (): Promise<ITechnicianCertificate[]> =>
    requests.get("/techniciancertificates"),
  search: (params: URLSearchParams): Promise<ITechnicianCertificate[]> =>
    axios.get("/techniciancertificates", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/techniciancertificates/${id}`),
  // create: (techniciancertificate: ITechnicianCertificate) => requests.post('/techniciancertificates', techniciancertificate),
  create: (techniciancertificate: ITechnicianCertificate[]) =>
    requests.post("/techniciancertificates", techniciancertificate),
  update: (techniciancertificate: ITechnicianCertificate) =>
    requests.put(
      `/techniciancertificates/${techniciancertificate.id}`,
      techniciancertificate
    ),
  delete: (id: string) => requests.del(`/techniciancertificates/${id}`),
  verify: (id: string) => requests.put(`/techniciancertificates/verify/${id}`, ""),
};

const ThirdParties = {
  list: (): Promise<IThirdparty[]> => requests.get("/ThirdParties"),
  sort: (type: string): Promise<IThirdparty[]> =>
    requests.get(`/ThirdParties/${type}`),
  //details: (name: string) => requests.get(`/ThirdParties/${name}}`),
  create: (thirdparty: IThirdparty) =>
    requests.post("/ThirdParties", thirdparty),
  update: (thirdparty: IThirdparty) =>
    requests.put(`/ThirdParties/${thirdparty.companyName}`, thirdparty),
  delete: (name: string) => requests.del(`/ThirdParties/${name}`),
};

const ProjectVendors = {
  list: (projectId: string): Promise<IProjectVendor[]> =>
    requests.get(`/projectvendors/${projectId}`),
  details: (id: string) => requests.get(`/projectvendors/${id}`),
  create: (projectvendor: IProjectVendor) =>
    requests.post("/projectvendors", projectvendor),
  update: (projectvendor: IProjectVendor) =>
    requests.put(`/projectvendors/${projectvendor.id}`, projectvendor),
  delete: (id: string) => requests.del(`/projectvendors/${id}`),
};

const ProjectScopes = {
  list: (projectId: string): Promise<IProjectScope[]> =>
    requests.get(`/projectscopes/${projectId}`),
  create: (projectscope: IProjectScope) =>
    requests.post("/projectscopes", projectscope),
  update: (projectscope: IProjectScope) =>
    requests.put(`/projectscopes/${projectscope.id}`, projectscope),
  delete: (id: string) => requests.del(`/projectscopes/${id}`),
};

const Uploads = {
  uploadFile: (file: File[], name: string): Promise<IFile> =>
    requests.postForm(`/files`, file, name),
  uploadPhoto: (photo: File[], name: string, photoCategory: string): Promise<IPhoto> => 
    requests.postFormPhoto(`/photos`, photo, name, photoCategory),
};

const PhotoRequests = {
  list: (): Promise<IPhotoRequest[]> => requests.get("/photorequests"),
  details: (id: string) => requests.get(`/photorequests/${id}`),
  create: (photorequest: IPhotoRequest) =>
    requests.post("/photorequests", photorequest),
  update: (photorequest: IPhotoRequest) =>
    requests.put(`/photorequests/${photorequest.id}`, photorequest),
  delete: (id: string) => requests.del(`/photorequests/${id}`),
};

const ProjectPhotos = {
  list: (projectId: string): Promise<IProjectPhoto[]> =>
    requests.get(`/projectphotos/${projectId}`),
  select: (): Promise<IProject[]> => requests.get("/projectphotos"),
  // details: (id: string) => requests.get(`/projecttasks/${id}`),
  create: (projectphoto: IProjectPhoto) =>
    requests.post("/projectphotos", projectphoto),
  update: (projectphotoId: string, status: string) =>
    requests.put(`/projectphotos/${projectphotoId}`, {
      id: projectphotoId,
      status: status,
    }),
  delete: (id: string) => requests.del(`/projectphotos/${id}`),
};

const PhotoUploads = {
  list: (projectId: string): Promise<IPhotoUpload[]> =>
    requests.get(`/photouploads/${projectId}`),
};

const TechnicianProjects = {
  list: (projectId: string): Promise<ITechnicianSOR[]> =>
    requests.get(`/technicianprojects/${projectId}`),
  update: (claimedqtyUpdate: any[]) =>
    requests.put(`/technicianprojects`, claimedqtyUpdate),
};

const EmployeeCertificates = {
  list: (): Promise<ITechnicianCertificate[]> =>
    requests.get("/employeecertificates"),
  // details: (id: string) => requests.get(`/techniciancertificates/${id}`),
  create: (employeecertificate: ITechnicianCertificate) =>
    requests.post("/employeecertificates", employeecertificate),
  // create: (techniciancertificate: ITechnicianCertificate[]) => requests.post('/techniciancertificates', techniciancertificate),
  update: (employeecertificate: ITechnicianCertificate) =>
    requests.put(
      `/employeecertificates/${employeecertificate.id}`,
      employeecertificate
    ),
  // delete: (id: string) => requests.del(`/techniciancertificates/${id}`)
};

const EmployeeProfiles = {
  details: () => requests.get(`employeeprofiles`),
  update: (employee: IEmployee) =>
    requests.put(`/employeeprofiles/${employee.email}`, employee),
};

const FttbHours = {
  list: (): Promise<IFttbHour[]> => requests.get("/fttbhours"),
  search: (params: URLSearchParams): Promise<IFttbHour[]> =>
    axios.get("/fttbhours", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/fttbhours/${id}`),
  create: (fttbhour: IFttbHour[]) => requests.post("/fttbhours", fttbhour),
  update: (fttbhour: IFttbHour) =>
    requests.put(`/fttbhours/${fttbhour.id}`, fttbhour),
  delete: (id: string) => requests.del(`/fttbhours/${id}`),
};

const FttcHours = {
  list: (): Promise<IFttcHour[]> => requests.get("/fttchours"),
  search: (params: URLSearchParams): Promise<IFttcHour[]> =>
    axios.get("/fttchours", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/fttchours/${id}`),
  create: (fttchour: IFttcHour[]) => requests.post("/fttchours", fttchour),
  update: (fttchour: IFttcHour) =>
    requests.put(`/fttchours/${fttchour.id}`, fttchour),
  delete: (id: string) => requests.del(`/fttchours/${id}`),
};

const BdodJobs = {
  list: (): Promise<IBdodJob[]> => requests.get("/bdodjobs"),
  search: (params: URLSearchParams): Promise<IBdodJob[]> =>
    axios.get("/bdodjobs", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/bdodjobs/${id}`),
  create: (bdodjob: IBdodJob[]) => requests.post("/bdodjobs", bdodjob),
  update: (bdodjob: IBdodJob) =>
    requests.put(`/bdodjobs/${bdodjob.id}`, bdodjob),
  delete: (id: string) => requests.del(`/bdodjobs/${id}`),
};

const UnifyJobs = {
  list: (): Promise<IUnifyJob[]> => requests.get("/unifyjobs"),
  search: (params: URLSearchParams): Promise<IUnifyJob[]> =>
    axios.get("/unifyjobs", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/unifyjobs/${id}`),
  create: (unifyjob: IUnifyJob[]) => requests.post("/unifyjobs", unifyjob),
  update: (unifyjob: IUnifyJob) =>
    requests.put(`/unifyjobs/${unifyjob.id}`, unifyjob),
  delete: (id: string) => requests.del(`/unifyjobs/${id}`),
};

const PayrollCycles = {
  list: (): Promise<IPayrollCycle[]> => requests.get("/payrollcycles"),
};

const TechnicianCertificatePhotos = {
  list: (email: string, certificateId: string): Promise<ITechnicianCertificatePhoto[]> =>
    requests.get(`/techniciancertificatephotos/${email}/${certificateId}`),
  delete: (id: string) => requests.del(`/techniciancertificatephotos/${id}`),
  batch: (email: string): Promise<IFile> => requests.get(`/techniciancertificatephotos/${email}`),
  archive: (email: string, certificateId: string) => requests.put(`/techniciancertificatephotos/${email}/${certificateId}`, {}),
};

const SiteVodafones = {
  search: (params: URLSearchParams): Promise<ISiteVodafone[]> =>
    axios.get("/sitevodafones", { params: params }).then(responseBody),
  details: (id: string) => requests.get(`/sitevodafones/${id}`),
  update: (sitevodafone: ISiteVodafone) =>
    requests.put(`/sitevodafones/${sitevodafone.id}`, sitevodafone),
  delete: (id: string) => requests.del(`/sitevodafones/${id}`),
  listhistory: (siteVodafoneId: string): Promise<ISiteVodafoneHistory[]> =>
    requests.get(`/sitevodafones/listhistory?sitevodafoneid=${siteVodafoneId}`),
};

const Transmissions = {
  list: (): Promise<ITransmission[]> => requests.get("/transmissions"),
  details: (id: string) => requests.get(`/transmissions/${id}`),
  create: (transmission: ITransmission) =>
    requests.post("/transmissions", transmission),
  update: (transmission: ITransmission) =>
    requests.put(`/transmissions/${transmission.id}`, transmission),
  delete: (id: string) => requests.del(`/transmissions/${id}`),
  generate: () => requests.get(`/transmissions/code`),
};

const Assets = {
  list: (): Promise<IAsset[]> => requests.get("/assets"),
  details: (id: string) => requests.get(`/assets/${id}`),
  create: (file: File[], asset: IAsset) =>
    requests.postFormAsset(`/assets`, file, asset),
  update: (file: File[], asset: IAsset) =>
    requests.postFormAsset(`/assets/${asset.id}`, file, asset),
  delete: (id: string) => requests.del(`/assets/${id}`),
  generate: () => requests.get(`/assets/code`),
};

const TechnicianTransmissions = {
  list: (): Promise<ITransmissionProjectWithFile[]> =>
    requests.get("/techniciantransmissions"), 
  create: (transmissionchecklist: ITransmissionChecklist) =>
    requests.post("/techniciantransmissions", transmissionchecklist),
  uploadTransmissionPhoto: (photo: File[], id: string, siteId: string, photoCategory: string): Promise<IPhoto> => 
    requests.postFormTransmissionPhoto(`/techniciantransmissions/photos`, photo, id, siteId, photoCategory),
};

const EmployeeAssets = {
  list: (): Promise<IAsset[]> => requests.get("/employeeassets"),
};

const HumanResource = {
  certificateChart: (): Promise<ITechnicianCertificateName[]> =>
    requests.get("/humanresource/certificatecharts"), 
};

const ProjectManagement = {
  listCount: (type: string) =>
    requests.get(`/projectmanagement/count/${type}`), 
};

const MinorTasks = {
  list: (projectId: string): Promise<IProjectTask[]> =>
    requests.get(`/minortasks/${projectId}`),
  create: (projecttask: IProjectTask) =>
    requests.post("/minortasks", projecttask),
  update: (projecttask: IProjectTask) =>
    requests.put(`/minortasks/${projecttask.id}`, projecttask),
  delete: (id: string) => requests.del(`/minortasks/${id}`), 
};

const ProjectBogs = {
  list: (projectId: string): Promise<IProjectBog[]> =>
    requests.get(`/projectbogs/${projectId}`),
  create: (projectbog: IProjectBog) =>
    requests.post("/projectbogs", projectbog),
  update: (projectbog: IProjectBog) =>
    requests.put(`/projectbogs/${projectbog.id}`, projectbog),
  delete: (id: string) => requests.del(`/projectbogs/${id}`), 
  report: (): Promise<IBogReport[]> => requests.get("/projectbogs"),
};

const EmployeeTimeSheets = {
  list: (): Promise<ITimeSheet[]> => 
  requests.get(`/employeetimesheets`),
  update: (employeeTimeSheets: ITimeSheet[]) =>
    requests.put(`/employeetimesheets`, employeeTimeSheets),
};

const TimeSheets = {
  list: (employee: string, payrun: string): Promise<ITimeSheet[]> => 
  requests.get(`/timesheets?employee=${employee}&payrun=${payrun}`),
  update: (payrun: string, email: string, status: string, remarks: any[]) =>
    requests.put(`/timesheets`, {payRun: payrun, email: email, status: status, remarks: remarks}),
};

const TimeSheetPays = {
  list: (): Promise<ITimeSheet[]> => requests.get(`/timesheetpays`),
  update: (payrun: string, email: string, status: string) =>
    requests.put(`/timesheetpays`, {payRun: payrun, email: email, status: status}),
};

const ProjectDocuments = {
  list: (projectId: string): Promise<IProjectDocument[]> =>
    requests.get(`/projectdocuments/${projectId}`),
  create: (projectdocuments: IProjectDocument) =>
    requests.post("/projectdocuments", projectdocuments),
  delete: (id: string) => requests.del(`/projectdocuments/${id}`),
};

const JobPositions = {
  list: (): Promise<IJobPosition[]> => requests.get("/jobpositions"),
  details: (id: string) => requests.get(`/jobpositions/${id}`),
  create: (jobposition: IJobPosition) => requests.post("/jobpositions", jobposition),
  update: (jobposition: IJobPosition) =>
    requests.put(`/jobpositions/${jobposition.id}`, jobposition),
  delete: (id: string) => requests.del(`/jobpositions/${id}`),
};

const ProjectTimes = {
  list: (): Promise<IProjectTime[]> => requests.get("/projecttimes"),
  details: (id: string) => requests.get(`/projecttimes/${id}`),
  create: (projecttime: IProjectTime) => requests.post("/projecttimes", projecttime),
  update: (projecttime: IProjectTime) => requests.put(`/projecttimes/${projecttime.id}`, projecttime),
  delete: (id: string) => requests.del(`/projecttimes/${id}`),
};

export default {
  User,
  Projects,
  SORLists,
  ProjectTasks,
  TaskAssignments,
  ProjectLogs,
  ProjectPermits,
  ProjectNotes,
  Warehouses,
  Invoices,
  WarehouseLogs,
  ProjectStocks,
  Certificates,
  TechnicianCertificates,
  ThirdParties,
  ProjectVendors,
  ProjectScopes,
  Uploads,
  PhotoRequests,
  ProjectPhotos,
  PhotoUploads,
  Employees,
  Customers,
  TechnicianProjects,
  EmployeeCertificates,
  FttbHours,
  FttcHours,
  PayrollCycles,
  EmployeeProfiles,
  TechnicianCertificatePhotos,
  BdodJobs,
  SiteVodafones,
  UnifyJobs,
  Transmissions,
  Assets,
  EmployeeAssets,
  TechnicianTransmissions,
  HumanResource,
  MobileAssignments,
  ProjectManagement,
  MinorTasks,
  ProjectBogs,
  EmployeeTimeSheets,
  TimeSheets,
  TimeSheetPays,
  ProjectDocuments,
  JobPositions,
  ProjectTimes,
};
